import { actionProps } from "@/lib/controlAnalytics";
import { useState } from "react";
import Modal, { RadioCard } from "@/components/common/Modal";
import { buttonClass } from "@/components/common/controls";
import { SKIP_REASONS, type Application, type SkipReason } from "@/lib/api/envoy";
import { useApplicationActions, useSkipPosting } from "@/lib/hooks/envoy";
import { SKIP_REASON_LABEL } from "@/lib/skipReasons";

/** A posting to skip by the catalogue's id, whether or not it has an application yet. */
export interface SkippedPosting {
    posting_id: string;
    title: string;
    company: string;
}

/**
 * Skip with a reason from the fixed list. The envoy never matches it
 * again. Given an application, that application is declined; given a
 * posting (an Openings card), the posting is, by its id.
 */
export default function SkipDialog({
    application = null,
    posting = null,
    onClose,
}: {
    application?: Application | null;
    posting?: SkippedPosting | null;
    onClose: () => void;
}) {
    const { skip } = useApplicationActions();
    const skipPosting = useSkipPosting();
    const [reason, setReason] = useState<SkipReason>("NOT_A_FIT");
    const subject = application ?? posting;
    // Each posting asked about starts on the first reason, never the last one's.
    const [asked, setAsked] = useState(subject?.posting_id ?? null);
    if ((subject?.posting_id ?? null) !== asked) {
        setAsked(subject?.posting_id ?? null);
        setReason("NOT_A_FIT");
    }
    const pending = skip.isPending || skipPosting.isPending;
    const confirm = () => {
        if (application) {
            skip.mutate(
                { applicationId: application.application_id, reason },
                { onSuccess: onClose },
            );
        } else if (posting) {
            skipPosting.mutate({ postingId: posting.posting_id, reason }, { onSuccess: onClose });
        }
    };

    return (
        <Modal
            analyticsContext={{
                source: "SkipDialog",
                section: "skip",
                application_id: application?.application_id,
                posting_id: subject?.posting_id,
            }}
            open={subject !== null}
            onClose={onClose}
            width={440}
            title="Skip this posting"
            description={
                subject && (
                    <>
                        <span className="font-semibold text-fg">{subject.title}</span> at{" "}
                        {subject.company}. Why not? Your envoy never matches it again, and the
                        reason stays in the record.
                    </>
                )
            }
            footer={
                <>
                    <button
                        {...actionProps("skip_cancelled", {
                            source: "SkipDialog",
                            application_id: application?.application_id,
                            posting_id: subject?.posting_id,
                            option: reason,
                        })}
                        type="button"
                        onClick={onClose}
                        className={buttonClass("ghost", "lg", "h-[38px] rounded-[11px]")}
                    >
                        Keep it
                    </button>
                    <button
                        {...actionProps("skip_confirm_clicked", {
                            source: "SkipDialog",
                            application_id: application?.application_id,
                            posting_id: subject?.posting_id,
                            option: reason,
                        })}
                        type="button"
                        disabled={pending || !subject}
                        onClick={confirm}
                        className="inline-flex h-[38px] max-[760px]:min-h-11 items-center rounded-[11px] bg-fg px-4 text-[13px] font-semibold text-on-fg disabled:opacity-60"
                    >
                        Skip
                    </button>
                </>
            }
        >
            <div role="radiogroup" aria-label="Reason" className="flex flex-col gap-1.5">
                {SKIP_REASONS.map((option) => (
                    <RadioCard
                        analyticsOption={option}
                        key={option}
                        name="skip-reason"
                        checked={reason === option}
                        onChange={() => setReason(option)}
                    >
                        {SKIP_REASON_LABEL[option]}
                    </RadioCard>
                ))}
            </div>
        </Modal>
    );
}
